import { useEffect, useState } from 'react';
import api from './api';

/**
 * Who the signed-in person may open the Activity of: Admin and HR anyone,
 * a Manager or Team Lead their own team (server/utils/personActivity.js says
 * exactly who). Everyone else is never asked about, and gets plain names.
 */

export const ACTIVITY_ROLES = ['ADMIN', 'HR', 'MANAGER', 'TEAM LEAD'];

const known = new Map(); // userId -> true/false, for this page load

/** The subset of `ids` the caller may open, as a Set; empty until known. */
export const useActivityAccess = (ids, myRole) => {
    const key = (ids || []).filter(Boolean).map(String).sort().join(',');
    const [allowed, setAllowed] = useState(() => new Set(key.split(',').filter((id) => known.get(id))));

    useEffect(() => {
        const list = key ? key.split(',') : [];
        if (!ACTIVITY_ROLES.includes(myRole) || !list.length) {
            setAllowed(new Set());
            return undefined;
        }
        const fromCache = () => new Set(list.filter((id) => known.get(id)));
        const missing = list.filter((id) => !known.has(id));
        if (!missing.length) {
            setAllowed(fromCache());
            return undefined;
        }
        let live = true;
        api.post('/employees/activity-access', { ids: missing })
            .then(({ data }) => {
                const yes = new Set((data?.allowed || []).map(String));
                missing.forEach((id) => known.set(id, yes.has(id)));
                if (live) setAllowed(fromCache());
            })
            .catch(() => { if (live) setAllowed(fromCache()); });
        return () => { live = false; };
    }, [key, myRole]);

    return allowed;
};

// --- Office calendar (India), matching the server ---

const OFFICE_TZ = 'Asia/Kolkata';

/** Today in the office, 'YYYY-MM-DD'. */
export const officeToday = (now = new Date()) => now.toLocaleDateString('en-CA', { timeZone: OFFICE_TZ });

/** The weeks of a month, Monday first, as 'YYYY-MM-DD' or null for padding. */
export const monthGrid = (year, month) => {
    const first = new Date(Date.UTC(year, month - 1, 1));
    const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const lead = (first.getUTCDay() + 6) % 7;
    const cells = Array(lead).fill(null);
    for (let d = 1; d <= days; d += 1) {
        cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
};

export const shiftMonth = ({ year, month }, by) => {
    const d = new Date(Date.UTC(year, month - 1 + by, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
};

export const monthKey = ({ year, month }) => `${year}-${String(month).padStart(2, '0')}`;

/** '2026-10-17' -> 'Saturday, 17 October 2026'. */
export const longDay = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
});

/** A row's time of day in the office, '3:05 pm'. */
export const officeTime = (iso) => new Date(iso).toLocaleTimeString('en-IN', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: OFFICE_TZ
});
